// ManageTips.js (updated with mobile responsiveness)
import React, { useState, useEffect } from 'react';
import { Row, Col, Container, Card, Spinner, Alert, Button, Badge } from 'react-bootstrap';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import './ManageTips.css';
import BaseUrl from '../../ApiServices/BaseUrl';
import { FaEdit, FaTrash } from "react-icons/fa";

function ManageTips() {
  const navigate = useNavigate();
  const [tipsData, setTipsData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch tips data from backend
  const fetchTipsData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BaseUrl}/api/tips`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setTipsData(data);
    } catch (err) {
      console.error("Error fetching tips data:", err);
      Swal.fire({
        title: 'Error!',
        text: 'Error fetching tips: ' + err.message,
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTipsData();
  }, []);

  // Function to truncate text for card display
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  const handleCreateTip = () => {
    navigate('/createtip');
  };

  const handleEditTip = (tipId) => {
    navigate(`/createtip/${tipId}`);
  };

  const handleDeleteTip = async (tipId, tipTitle) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${tipTitle}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${BaseUrl}/api/tips/${tipId}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          Swal.fire({
            title: 'Deleted!',
            text: 'Tip has been deleted successfully.',
            icon: 'success',
            confirmButtonColor: '#3085d6',
          });
          // Refresh the tips list
          fetchTipsData();
        } else {
          throw new Error('Failed to delete tip');
        }
      } catch (error) {
        Swal.fire({
          title: 'Error!',
          text: 'Error deleting tip: ' + error.message,
          icon: 'error',
          confirmButtonColor: '#d33',
        });
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      published: { variant: 'success', text: 'Published' },
      draft: { variant: 'warning', text: 'Draft' },
      archived: { variant: 'secondary', text: 'Archived' }
    };
    
    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant} className="status-badge">{config.text}</Badge>;
  };

  const getCategoryBadge = (category) => {
    const categoryColors = {
      business: 'primary',
      technology: 'info',
      marketing: 'success',
      sales: 'warning',
      finance: 'danger',
      management: 'dark'
    };
    
    const color = categoryColors[category?.toLowerCase()] || 'secondary';
    return <Badge bg={color} className="category-badge">{category}</Badge>;
  };

  return (
    <>
      <AdminNavbar />
      <Container className="manage-tips-container">
        {/* Header with Create Tip Button - Mobile Responsive */}
        <div className="tips-page-header">
          <h2 className="tips-page-title">Manage Tips</h2>
          <Button 
            variant="primary" 
            onClick={handleCreateTip}
            className="create-tip-btn"
          >
            Create New Tip
          </Button>
        </div>

        {/* Tips Cards Section */}
        <div className="tips-cards-section">
          <h3 className="section-subtitle">All Tips</h3>
          
          {loading ? (
            <div className="loading-container">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <Row className="tips-grid">
              {tipsData.length > 0 ? (
                tipsData.map(tip => (
                  <Col xs={12} sm={6} lg={4} className="tip-col" key={tip.id}>
                    <Card className="tip-card">
                      <Card.Body className="tip-card-body">
                        {/* Header with badges and section */}
                        <div className="tip-card-header">
                          <div className="badges-container">
                            {getStatusBadge(tip.status)}
                            {getCategoryBadge(tip.category)}
                          </div>
                          <span className="section-label">
                            {tip.section}
                          </span>
                        </div>
                        
                        {/* Title */}
                        <Card.Title className="tip-title">
                          {tip.title}
                        </Card.Title>
                        
                        {/* Excerpt */}
                        <Card.Text className="tip-excerpt">
                          {truncateText(tip.excerpt, 120)}
                        </Card.Text>
                        
                        {/* Content Preview */}
                        <Card.Text className="tip-content-preview">
                          <strong>Content:</strong> {truncateText(tip.content, 100)}
                        </Card.Text>
                        
                        {/* Footer */}
                        <div className="tip-card-footer">
                          {tip.tags && (
                            <div className="tags-section">
                              <small className="tags-label">
                                <strong>Tags:</strong> {truncateText(tip.tags, 50)}
                              </small>
                            </div>
                          )}
                          
                          <div className="footer-actions">
                            <small className="slug-preview">
                              <strong>Slug:</strong> {truncateText(tip.slug, 30)}
                            </small>
                            <div className="action-buttons">
                              <Button 
                                variant="" 
                                size="sm" 
                                className=" edit-btn"
                                onClick={() => handleEditTip(tip.id)}
                                title="Edit Tip"
                              >
                                <FaEdit />
                              </Button>
                              <Button 
                                variant="" 
                                size="sm"
                                className=" delete-btn"
                                onClick={() => handleDeleteTip(tip.id, tip.title)}
                                title="Delete Tip"
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))
              ) : (
                <Col xs={12}>
                  <Alert variant="info" className="no-tips-alert">
                    No tips found. Click "Create New Tip" to create your first tip.
                  </Alert>
                </Col>
              )}
            </Row>
          )}
        </div>
      </Container>
    </>
  );
}

export default ManageTips;